import React, { ChangeEvent, CSSProperties, RefObject, useEffect, useRef, useState, KeyboardEvent } from 'react'

import format from 'date-fns/fp/format'
import isValid from 'date-fns/fp/isValid'
import parse from 'date-fns/fp/parse'

import { DATE_FORMAT } from '../Uniform/constants'
import { Datepicker } from './Datepicker'
import { Mode } from './Datepicker/enums'

export interface IDatepickerOptions {
  autoMove?: boolean
  enableYearsShift?: boolean
  disableInModes?: Mode[]
}

export interface IProps {
  containerClass?: string
  modalClass?: string
  outerClass: string
  inputClass?: string
  id?: string
  name?: string
  value: Date
  required?: boolean
  placeholder?: string
  defaultValue?: Date
  disabled?: boolean
  disableDates?: (date: Date | string) => boolean
  useDatepicker?: boolean
  enableBlankValue?: boolean
  datepickerOptions?: IDatepickerOptions
  rf?: string
  isValid?: boolean
  onChange: (value: Date | null) => void
}

/**
 * Standard date input with datepicker modal.
 *
 *
 * @param containerClass (string, required) - Main component css class. Default value is `cs-dateinput__container`
 * @param modalClass (string, optional) - CssClass for datepicker modal. Default value is `cs-rdatepicker__modal`
 * @param outerClass (string, required) - Css class for input outer container
 * @param inputClass (string, optional) - Css input class
 * @param id (string, required) - Input id
 * @param name (string, required) - Input name
 * @param value (Date, required) - Contolled input value
 * @param defaultValue (Date, optional) - Default value. This value is set to current date if is not assigned
 * @param disabled (string, optional) - Set the input and datepicker as disabled
 * @param required (boolean, optional) - Input required option.
 * @param disableDates (void: Date, optional) - Function which can disable specific dates
 * This function must have date on input and must return boolean value
 * @param useDatepicker (boolean, optional) - Enable datepicker. Default value is `false`
 * @param datepickerOptions (object, optional) - Object with additional datepicker settings
 * @param datepickerOptions.autoMove (boolean, optional) - Enable / disable auto movement in datepicker.
 * Default value is `false`
 * @param datepickerOptions.enableYearsShift (boolean, optional) - Enable / disable years shift in datepicker years mode
 * Default value is `false`
 * @param datepickerOptions.disableInModes (Mode[], optional) - Array with datepicker modes (day, month, years)
 * which will evaluate `disableDates` param
 * @param rf (string, optional) - Optional tracking id fro automated tests
 * @param placeholder (string, optional) - Optional date input placeholder
 * @param onChange (void: Date, required) - On change handler
 *
 */

export const BLANK_VALUE = ''

export default function DateInput(props: IProps) {
  const containerClass = props.containerClass || 'cs-dateinput__container'
  const modalClass = props.modalClass || 'cs-rdatepicker__modal'
  const inputClass = props.inputClass || 'csas-uib-datepicker-input'

  const defaultValue =
    !props.enableBlankValue && props.defaultValue ? format(DATE_FORMAT)(props.defaultValue) : BLANK_VALUE

  const getStateDefault = () => {
    if (props.value && isValid(props.value)) {
      return format(DATE_FORMAT)(props.value)
    }

    if (defaultValue) {
      return defaultValue
    }

    return BLANK_VALUE
  }

  const datepickerRef: RefObject<HTMLDivElement> = useRef(null)
  const [value, setValue] = useState(getStateDefault())

  const [visible, setVisible] = useState(false)
  const [errorDateSelected, setErrorDateSelected] = useState(false)

  useEffect(() => {
    const formattedPropsValue = isValid(props.value) ? format(DATE_FORMAT)(props.value) : ''
    if (formattedPropsValue !== value) {
      setValue(formattedPropsValue)
    }
  }, [props.value])

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick)
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [])

  const handleOutsideClick = (e: any) => {
    if ((datepickerRef.current && datepickerRef.current.contains(e.target)) || errorDateSelected) {
      return
    }
    if (visible) {
      props.onChange(parse(new Date())(DATE_FORMAT)(value))
    }
    setVisible(false)
  }

  const getDefaultValue = () => (defaultValue ? parse(new Date())(DATE_FORMAT)(defaultValue) : null)

  const parseValueFromString = (someString: string) => {
    let dateValue: Date | null
    if (isValid(parse(new Date())('dd.MM.yyyy')(someString))) {
      dateValue = parse(new Date())('dd.MM.yyyy')(someString)
    } else if (isValid(parse(new Date())('dd.M.yyyy')(someString))) {
      dateValue = parse(new Date())('dd.M.yyyy')(someString)
    } else {
      return getDefaultValue()
    }

    if (
      dateValue &&
      (dateValue.getFullYear() < new Date().getFullYear() - 100 ||
        dateValue.getFullYear() > new Date().getFullYear() + 100)
    ) {
      dateValue.setFullYear(new Date().getFullYear())
    }

    if (dateValue && props.disableDates && props.disableDates.apply(this, [dateValue])) {
      dateValue = getDefaultValue()
    }

    return dateValue
  }

  const processBlankValue = () => {
    if (props.enableBlankValue || !props.defaultValue) {
      return null
    }

    if (!props.enableBlankValue && props.defaultValue) {
      return props.defaultValue
    }

    return null
  }

  const finalizeValue = () => {
    const checkedValue = value.toString().replace(/ /g, '')
    let dateValue: Date | null

    if (checkedValue !== BLANK_VALUE) {
      dateValue = parseValueFromString(checkedValue)
    } else {
      dateValue = processBlankValue()
    }

    setValue(dateValue ? format(DATE_FORMAT)(dateValue) : BLANK_VALUE)
    props.onChange(dateValue)
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => setValue(e.target.value)

  const handleBlur = () => {
    finalizeValue()
  }

  const handleShowDatepicker = () => {
    if (props.useDatepicker && !props.disabled) {
      setVisible(!visible)
    }
  }

  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      finalizeValue()
    }
  }

  const handleCloseDatepicker = (date: Date) => {
    if (!errorDateSelected) {
      setValue(format(DATE_FORMAT)(date))
      setVisible(false)
      props.onChange(date)
    }
  }

  const handleChangePicker = (date: Date) => {
    if (props.disableDates && props.disableDates.apply(this, [date])) {
      setErrorDateSelected(true)
    } else {
      if (errorDateSelected) {
        setErrorDateSelected(false)
      }
      setValue(format(DATE_FORMAT)(date))
    }
  }

  const getInputStyle = (): CSSProperties => {
    if (props.isValid === false) {
      return {
        border: '1px solid #d0021b'
      }
    }

    return {}
  }

  return (
    <div className={containerClass}>
      <div className={props.outerClass} style={{ position: 'relative', display: 'inline-block' }}>
        <input
          type='text'
          name={props.name}
          id={props.id}
          required={props.required}
          className={inputClass}
          maxLength={12}
          placeholder={props.placeholder}
          value={value.toString()}
          disabled={props.disabled}
          onChange={handleChange}
          onBlur={handleBlur}
          data-testid={props.rf}
          onKeyPress={handleKeyPress}
          style={getInputStyle()}
          autoComplete='off'
        />
        <div onClick={handleShowDatepicker} className='cs-rdatepicker__input--clock'></div>
      </div>
      {visible && (
        <div className={modalClass} ref={datepickerRef}>
          <Datepicker
            {...props.datepickerOptions}
            disableDates={props.disableDates}
            date={parse(new Date())(DATE_FORMAT)(value)}
            onChange={handleChangePicker}
            onConfirmDate={handleCloseDatepicker}
          />
        </div>
      )}
    </div>
  )
}
